import Keycloak from 'keycloak-js';
import { inject, readonly, ref } from 'vue';

const AUTH_KEY = Symbol('auth');

let authInstance;

function getRequiredEnv(name) {
  const value = import.meta.env[name]?.trim();
  if (!value) {
    throw new Error(`Missing required auth environment variable: ${name}`);
  }

  return value;
}

function getKeycloakUrl() {
  return import.meta.env.VITE_KEYCLOAK_URL?.trim() || `${window.location.origin}/auth`;
}

function buildUserProfile(tokenParsed) {
  if (!tokenParsed) {
    return null;
  }

  return {
    id: tokenParsed.sub,
    username: tokenParsed.preferred_username ?? '',
    email: tokenParsed.email ?? '',
    name:
      tokenParsed.name ??
      tokenParsed.preferred_username ??
      tokenParsed.email ??
      tokenParsed.sub,
  };
}

function createAuth() {
  if (authInstance) {
    return authInstance;
  }

  const keycloak = new Keycloak({
    url: getKeycloakUrl(),
    realm: getRequiredEnv('VITE_KEYCLOAK_REALM'),
    clientId: getRequiredEnv('VITE_KEYCLOAK_CLIENT_ID'),
  });

  const isReady = ref(false);
  const isAuthenticated = ref(false);
  const user = ref(null);
  const error = ref('');

  let initPromise;

  const syncState = () => {
    isAuthenticated.value = Boolean(keycloak.authenticated);
    user.value = buildUserProfile(keycloak.tokenParsed);
  };

  const init = async () => {
    if (!initPromise) {
      initPromise = keycloak
        .init({
          pkceMethod: 'S256',
          checkLoginIframe: false,
        })
        .then(() => {
          syncState();
          isReady.value = true;
        })
        .catch((initError) => {
          error.value = initError instanceof Error ? initError.message : String(initError);
          throw initError;
        });
    }

    await initPromise;
  };

  const login = async ({ redirectPath = '/chat' } = {}) => {
    await init();
    await keycloak.login({
      redirectUri: `${window.location.origin}${redirectPath}`,
    });
  };

  const logout = async ({ redirectPath = '/' } = {}) => {
    await init();
    await keycloak.logout({
      redirectUri: `${window.location.origin}${redirectPath}`,
    });
    syncState();
  };

  const changePassword = async () => {
    await init()
    const accountUrl = await keycloak.createLoginUrl({
      redirectUri: `${window.location.origin}/account`,
      action: 'UPDATE_PASSWORD',
    })
    window.location.href = accountUrl

  }
  const getAccessToken = async () => {
    await init();

    if (!keycloak.authenticated) {
      return null;
    }

    try {
      await keycloak.updateToken(30);
      syncState();
      return keycloak.token ?? null;
    } catch (tokenError) {
      error.value = tokenError instanceof Error ? tokenError.message : String(tokenError);
      return null;
    }
  };

  const hasRealmRole = (role) => keycloak.hasRealmRole(role);

  authInstance = {
    error: readonly(error),
    hasRealmRole,
    init,
    isAuthenticated: readonly(isAuthenticated),
    isReady: readonly(isReady),
    login,
    logout,
    changePassword,
    getAccessToken,
    user: readonly(user),
  };

  return authInstance;
}

export function installAuth(app) {
  const auth = createAuth();
  app.provide(AUTH_KEY, auth);
  return auth;
}

export function useAuth() {
  const auth = inject(AUTH_KEY, null);
  if (!auth) {
    throw new Error('Auth plugin is not installed');
  }

  return auth;
}

export function getAuth() {
  return createAuth();
}
